import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { DatePickerComponent } from '../../../@commons/@components/date-picker/date-picker.component';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { PosthogService } from '../../../@commons/@services/front/posthog.service';
import { StoreReviewService } from '../../../@commons/@services/front/store-review.service';
import { Plant } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/plant.entity';
import { PlantHarvestApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant-harvest/plant-harvest-api';
import { HarvestUnit } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/harvest-unit.enum';

/**
 * Récolter (#206b) : enregistre une récolte échelonnée. La plante RESTE active (≠ « Terminer »).
 * Permet de noter ce qui a été récolté (date + quantité + unité + note).
 */
import { LabelPipe } from '../../../@commons/pipes/LabelPipe';
@Component({
  selector: 'vw-harvest-log-toast',
  imports: [LabelPipe, SharedModule, TranslatePipe, DatePickerComponent, CommonModule, FormsModule],
  providers: [{ provide: PlantHarvestApi, useClass: PlantHarvestApi }],
  templateUrl: './harvest-log-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './harvest-log-toast.component.scss',
})
export class HarvestLogToastComponent extends BaseToastComponent {
  @Input() plant!: Plant;
  @Output() logged = new EventEmitter<void>();

  date: Date = new Date();
  quantity?: number;
  selectedUnit: string | null = null;
  note = '';
  submitLoading = false;
  /** Récolte refusée par le serveur : dit au-dessus du bouton. */
  submitFailed = false;
  unitOptions: { label: string; value: string }[] = [];

  constructor(
    private plantHarvestApi: PlantHarvestApi,
    private storageService: StorageService,
    private posthog: PosthogService,
    private translate: TranslateService,
    private storeReview: StoreReviewService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.unitOptions = [
      HarvestUnit.PIECE,
      HarvestUnit.GRAM,
      HarvestUnit.KILOGRAM,
      HarvestUnit.BUNCH,
      HarvestUnit.LITER,
    ].map((value) => ({
      value,
      label: this.translate.instant('garden.harvest.unit.' + value),
    }));
  }

  onDateChange(date: Date): void {
    this.date = date;
  }

  /** Sélection directe de l'unité (un tap). Re-tap = désélection (l'unité est optionnelle). */
  selectUnit(value: string): void {
    this.selectedUnit = this.selectedUnit === value ? null : value;
  }

  async submit(): Promise<void> {
    if (this.submitLoading) return;
    this.submitLoading = true;
    this.submitFailed = false;
    const localDate = new Date(
      this.date.getTime() - this.date.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split('T')[0];
    try {
      await this.plantHarvestApi.create(
        {
          plantUid: this.plant.uid,
          harvestedDate: localDate,
          quantity:
            this.quantity !== undefined && this.quantity !== null && `${this.quantity}` !== ''
              ? Number(this.quantity)
              : undefined,
          unit: (this.selectedUnit as HarvestUnit) ?? undefined,
          note: this.note?.trim() || undefined,
        },
        this.storageService.getCurrentLanguage(),
      );
      this.posthog.capture('plant_harvest_logged', { plantUid: this.plant.uid });
      this.logged.emit();
      this.onClose();
      // Une récolte notée : le moment où l'app vient de rendre service (CR-06).
      void this.storeReview.afterHarvestLogged();
    } catch (e) {
      // Refusée : la feuille reste ouverte avec la saisie, et le dit.
      console.error('HarvestLogToastComponent: failed to log harvest', e);
      this.submitFailed = true;
    } finally {
      this.submitLoading = false;
    }
  }
}
